import React, { useEffect, useReducer, useState } from 'react';
import { connect } from 'react-redux';
import locale from "locale";
import clone from 'lodash.clonedeep';
import * as CONSTANTS from './constants';
import {
    Col,
    Row,
    FormGroup,
    ControlLabel,
    FormControl,
    Checkbox,
    Tabs,
    Tab,
    Breadcrumb,
} from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { pages, pagesSettingsUpdate, eventModuleEpisodeUpdate } from '../../../../../../api/cp';
import { availableTypesBackground } from '../../../EventSettings/models';
import Loading from '../../../../Loading';
import ScheduleSettingViews from '../../../Widgets/Schedule/ScheduleSettingViews';
import CPSettingUploadImage from '../../../../UI/Settings/CPSettingUploadImage';
import Tooltip from "../../../../UI/Tooltip";
import CPSettingSize from '../../../../UI/Settings/CPSettingSize';
import FormControlLanguage from "../../../../UI/FormControlLanguage";
import SettingTabWrapper from '../../../Widgets/components/SettingTabWrapper';
import BlockSettings from '../../components/BlockSettings';

const ProgramsConstructorPage = ({ event, pageData = null }) => {
    const [pageDetails, setPegeDetais] = useState(null);
    const [programState, updateProgramState] = useReducer(
        (prev, next) => {
            return { ...prev, ...next };
        },
        clone(CONSTANTS.PROGRAM_INITIAL_MODEL)
    );
    const [pdfExportEnabled, setPdfExportEnabled] = useState(event.moduleEpisode.pdfExportEnabled || false);

    useEffect(async () => {
        if (pageData) {
            setPegeDetais(pageData);
            updateProgramState({ ...programState, ...pageData.settings });

            return;
        }

        const pagesList = await pages(event.eventKey);
        const programPage = pagesList?.find(({ type }) => type === 'program') ?? null;
        if (!!programPage) {
            setPegeDetais(programPage);
            updateProgramState({ ...programState, ...programPage.settings });
        }
    }, []);

    async function handleUpdateSettings(setting) {
        const formData = new FormData();
        Object.keys(setting).forEach((key) => {
            const value = setting[key];

            formData.append(
                key,
                Array.isArray(value) ? JSON.stringify(value) : value,
            );
        });

        const response = await pagesSettingsUpdate(pageDetails.id, formData);
        updateProgramState({ ...programState, ...response.settings });
    }

    async function handlePdfExportEnable(value) {
        setPdfExportEnabled(value);
        await eventModuleEpisodeUpdate(event.eventKey, { pdfExportEnabled: value });
    }

    if (!pageDetails) {
        return (<Loading />)
    }

    return (
        <section style={{ paddingBottom: '40px' }}>
            <Row>
                <Breadcrumb>
                    <LinkContainer to={`/event/${event.eventPassword}`}>
                        <Breadcrumb.Item>{locale('mainMenu.event')}</Breadcrumb.Item>
                    </LinkContainer>
                    <LinkContainer to={`/event/${event.eventPassword}/pages`}>
                        <Breadcrumb.Item>{locale('mainMenu.pages')}</Breadcrumb.Item>
                    </LinkContainer>
                    <Breadcrumb.Item active>{locale('mainMenu.schedule.programs')}</Breadcrumb.Item>
                </Breadcrumb>
            </Row>

            <Row>
                <Col xs={4}>
                    <Checkbox
                        checked={programState.showTitle}
                        onChange={(e) => handleUpdateSettings({ showTitle: e.target.checked })}
                    >
                        {locale('settings.showTitle')}
                    </Checkbox>
                    {programState.showTitle && (
                        <FormGroup>
                            <p>{locale('settings.title')}</p>
                            <FormControlLanguage
                                type="text"
                                values={{
                                    ru: programState.titleRu,
                                    en: programState.titleEn,
                                }}
                                onChange={{
                                    ru: (e) => updateProgramState({ titleRu: e.target.value }),
                                    en: (e) => updateProgramState({ titleEn: e.target.value }),
                                }}
                                onBlur={(e) => handleUpdateSettings({ ...programState, titleRu: programState.titleRu, titleEn: programState.titleEn })}
                            />
                        </FormGroup>
                    )}
                    <Checkbox
                        checked={programState.showSubtitle}
                        onChange={(e) => handleUpdateSettings({ showSubtitle: e.target.checked })}
                    >
                        {locale('settings.showSubtitle')}
                    </Checkbox>
                    {programState.showSubtitle && (
                        <FormGroup>
                            <p>{locale('settings.subtitle')}</p>
                            <FormControlLanguage
                                type="text"
                                values={{
                                    ru: programState.subtitleRu,
                                    en: programState.subtitleEn,
                                }}
                                onChange={{
                                    ru: (e) => updateProgramState({ subtitleRu: e.target.value }),
                                    en: (e) => updateProgramState({ subtitleEn: e.target.value }),
                                }}
                                onBlur={(e) => handleUpdateSettings({ ...programState, subtitleRu: programState.subtitleRu, subtitleEn: programState.subtitleEn })}
                            />
                        </FormGroup>
                    )}
                    <FormGroup controlId="formControlsSelect">
                        <ControlLabel>
                            {locale('program.view.types')}
                        </ControlLabel>
                        <ScheduleSettingViews
                            showLabel={false}
                            widgetViews={programState.scheduleViews}
                            onChange={handleUpdateSettings}
                        />
                    </FormGroup>
                    <FormGroup controlId="formControlsSelect">
                        <ControlLabel>
                            {locale('program.days.view')}
                        </ControlLabel>
                        <FormControl
                            value={programState.daysView}
                            componentClass="select"
                            placeholder="select"
                            onChange={(e) => handleUpdateSettings({ daysView: e.target.value })}
                        >
                            {
                                Object.values(CONSTANTS.PROGRAM_FILTERS_VIEWS).map((view) => (
                                    <option key={view} value={view}>
                                        {locale(`program.view.${view}`)}
                                    </option>
                                ))
                            }
                        </FormControl>
                    </FormGroup>
                    <FormGroup controlId="formControlsSelect">
                        <ControlLabel>
                            {locale('program.halls.view')}
                        </ControlLabel>
                        <FormControl
                            value={programState.hallsView}
                            componentClass="select"
                            placeholder="select"
                            onChange={(e) => handleUpdateSettings({ hallsView: e.target.value })}
                        >
                            {
                                [...Object.values(CONSTANTS.PROGRAM_FILTERS_VIEWS), CONSTANTS.PROGRAM_FILTERS_HIDE].map((view) => (
                                    <option key={view} value={view}>
                                        {locale(`program.view.${view}`)}
                                    </option>
                                ))
                            }
                        </FormControl>
                    </FormGroup>
                    <Checkbox
                        checked={programState.showAddToCalendar}
                        onChange={(e) => handleUpdateSettings({ showAddToCalendar: e.target.checked })}
                    >
                        {locale('program.calendar.buttons.show')}
                    </Checkbox>
                    <Checkbox
                        checked={programState.showModals}
                        onChange={(e) => handleUpdateSettings({ showModals: e.target.checked })}
                    >
                        {locale('program.modals.show')}
                    </Checkbox>
                    <Checkbox
                        checked={programState.showStatuses}
                        onChange={(e) => handleUpdateSettings({ showStatuses: e.target.checked })}
                    >
                        {locale('settings.showStatuses')}
                    </Checkbox>
                    <Checkbox
                        checked={programState.showFormats}
                        onChange={(e) => handleUpdateSettings({ showFormats: e.target.checked })}
                    >
                        {locale('settings.show.formats')}
                    </Checkbox>
                    <Checkbox
                        checked={pdfExportEnabled}
                        onChange={(e) => handlePdfExportEnable(e.target.checked)}
                    >
                        {locale('settings.pdfExportEnabled')}
                    </Checkbox>
                    <Checkbox
                        checked={programState.showWatchRecordButton}
                        onChange={(e) => handleUpdateSettings({ showWatchRecordButton: e.target.checked })}
                    >
                        {locale('program.watchRecording.button.show')}
                    </Checkbox>
                    <hr />
                    <Checkbox
                        checked={programState.showSubstrate}
                        onChange={(e) => handleUpdateSettings({ showSubstrate: e.target.checked })}
                    >
                        {locale('settings.showSubstrate')}
                    </Checkbox>
                    <CPSettingUploadImage
                        eventImage={programState.backgroundUrl}
                        title={locale('settings.uploadBackgroundImage')}
                        settingName="background"
                        availableTypes={availableTypesBackground}
                        hasCropper={false}
                        maxHeight="100px"
                        onUpdate={handleUpdateSettings}
                    />
                    <Checkbox
                        checked={programState.backgroundFixed}
                        onChange={(e) => handleUpdateSettings({ backgroundFixed: e.target.checked })}
                    >
                        {locale('settings.backgroundFixed')}
                        <Tooltip width="320px">
                            {locale('settings.backgroundFixedTooltip')}
                        </Tooltip>
                    </Checkbox>
                    <hr />
                    <h3>
                        {locale('program.settings.view')}
                    </h3>
                    <Tabs
                        id="tabs"
                        defaultActiveKey={1}
                    >
                        <Tab
                            eventKey={1}
                            title={locale('program.view.tiles')}
                        >
                            <br />
                            <FormGroup controlId="formControlsSelect">
                                <ControlLabel>
                                    {locale('program.settings.timelines')}
                                </ControlLabel>
                                <FormControl
                                    value={programState.intervals}
                                    componentClass="select"
                                    placeholder="select"
                                    onChange={(e) => handleUpdateSettings({ intervals: e.target.value })}
                                >
                                    {
                                        CONSTANTS.PROGRAM_TIME_INTERVALS.map((timeInterval) => (
                                            <option key={timeInterval} value={timeInterval}>
                                                {!!timeInterval ? locale(`program.time.minutes`, { minutes: timeInterval }) : locale('program.time.without.interval')}
                                            </option>
                                        ))
                                    }
                                </FormControl>
                            </FormGroup>
                        </Tab>
                        <Tab
                            eventKey={2}
                            title={locale('program.view.lines')}
                        >
                            <br />
                            <Checkbox
                                checked={programState.showDescription}
                                onChange={(e) => handleUpdateSettings({ showDescription: e.target.checked })}
                            >
                                {locale('program.description.show')}
                            </Checkbox>
                        </Tab>
                        <Tab
                            eventKey={3}
                            title={locale('program.view.grid')}
                        >
                            <br />
                            <CPSettingSize
                                name="gridCardHeightSize"
                                label={locale('settings.gridCardHeightSize')}
                                eventValue={programState.gridCardHeightSize}
                                defaultValue={programState.gridCardHeightSize}
                                hint={locale('settings.gridCardHeightSizeTooltip')}
                                onChange={handleUpdateSettings}
                            />
                            <Checkbox
                                checked={programState.showPhotos}
                                onChange={(e) => handleUpdateSettings({ showPhotos: e.target.checked })}
                            >
                                {locale('program.members.photo.show')}
                            </Checkbox>
                        </Tab>
                    </Tabs>
                    <SettingTabWrapper
                        label="BlockSettings"
                        title={locale('settings.tab.title.blocks')}
                    >
                        <BlockSettings
                            event={event}
                            pageId={pageData.id}
                            blocks={pageData.blocks}
                        />
                    </SettingTabWrapper>
                </Col>
                <Col xs={8}>
                    <iframe
                        name="programPage"
                        src={`/event/${event.eventKey}/program`}
                        style={{ width: '100%', height: '100dvh' }}
                        frameBorder="0"
                    />
                </Col>
            </Row>
        </section>
    )
};

const mapStateToProps = (store) => ({
    event: store.currentEvent,
});

export default connect(mapStateToProps)(ProgramsConstructorPage);
